import { useEffect, useState } from "react";
import { Spin, Table, Button, Popconfirm, Tag, message } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { useHistory } from "react-router-dom";

import ContentCard from "../../../components/ContentCard/ContentCard";
import DefaultCardHeader from "../../../components/DefaultCardHeader/DefaultCardHeader";
import "./PartnerOrganizationList.css";

const antIcon = <Spin size="large" />;

const formatDate = (value) => {
  if (!value) return "N/A";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "N/A" : d.toLocaleDateString();
};

const emptyCell = <span style={{ color: "rgba(0, 0, 0, 0.35)" }}>N/A</span>;

export default function PartnerOrganizationList() {
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [submissions, setSubmissions] = useState([]);

  const history = useHistory();

  const fetchAllSubmissions = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_MASTER}/partner/all`
      );
      const data = await response.json();

      if (data.success) {
        setSubmissions(data.data);
      }
    } catch {
      // fetch error
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAllSubmissions();
  }, []);

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_MASTER}/partner/${id}`,
        { method: "DELETE" }
      );
      const data = await response.json();

      if (data.success) {
        message.success("Submission deleted");
        setSubmissions((prev) => prev.filter((item) => item.id !== id));
      } else {
        message.error(data.message || "Failed to delete submission");
      }
    } catch {
      message.error("Failed to delete submission");
    }
    setDeletingId(null);
  };

  const columns = [
    {
      title: "Organization Name",
      dataIndex: "organizationName",
      key: "organizationName",
    },
    {
      title: "Contact Name",
      dataIndex: "contactName",
      key: "contactName",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      render: (text) => text || emptyCell,
    },
    {
      // The public form's funding tier picker. Older rows predate it and show
      // N/A; the fields that form no longer collects (organisation size, area
      // of interest, how they heard about us) are still on the details page.
      title: "Giving Level",
      dataIndex: "givingLevel",
      key: "givingLevel",
      render: (text) => (text ? <Tag color="blue">{text}</Tag> : emptyCell),
    },
    {
      title: "Submitted",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      defaultSortOrder: "descend",
      render: formatDate,
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      align: "center",
      render: (_, record) => (
        <Popconfirm
          title="Delete this submission?"
          description="This action cannot be undone."
          okText="Delete"
          okButtonProps={{ danger: true }}
          cancelText="Cancel"
          onConfirm={(e) => {
            e?.stopPropagation?.();
            handleDelete(record.id);
          }}
          onCancel={(e) => e?.stopPropagation?.()}
        >
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            loading={deletingId === record.id}
            onClick={(e) => e.stopPropagation()}
          />
        </Popconfirm>
      ),
    },
  ];

  return (
    <ContentCard
      isStickyHead
      headerContent={
        <div>
          <DefaultCardHeader>
            <div className="ambassadors-card__header">
              Partner Organization Submissions
            </div>
          </DefaultCardHeader>
        </div>
      }
    >
      <div style={{ paddingTop: 4 }}>
        <Table
          className="partner-submissions-table"
          rowKey="id"
          dataSource={submissions}
          columns={columns}
          pagination={{
            defaultPageSize: 15,
            pageSizeOptions: ["15", "25", "50", "100"],
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} submissions`,
          }}
          loading={{
            spinning: loading,
            indicator: <Spin indicator={antIcon} />,
          }}
          locale={{ emptyText: "No Partner Organizations Submissions found" }}
          onRow={(record) => {
            return {
              onClick: () => history.push(`/partners/${record.id}`),
            };
          }}
        />
      </div>
    </ContentCard>
  );
}
